/**
 * Discovery (DSPM) Dashboard - management-grade rework (2026-06-05).
 *
 * Goal: a CISO/DPO-readable command surface, not a metrics dump. Every tile is
 * (a) backed by REAL data and (b) clickable into the page that lets you act on
 * it. No fabricated numbers - sparse data renders honest empty/￼low states.
 *
 * Live data sources (all real, tenant-scoped). NOTE the /coverage endpoint
 * returns scan/classification counts + per-framework coverage - it does NOT
 * return byCategory/bySeverity/posture, so PII-by-type is derived from /assets
 * classifications and severity from /findings (no fabricated maps):
 *   • GET /v1/discovery/coverage → totalAssets, scannedAssets, classifiedAssets,
 *       byFramework[{framework,coverageScore,openFindings}]
 *   • GET /v1/discovery/findings → findings (severity, status, regs, createdAt)
 *   • GET /v1/discovery/assets   → data estate + PII-by-type (sourceType,
 *       sensitivity, owner, classifications, rowCount, sizeBytes, country, lastScanned)
 *   • GET /v1/discovery/scans    → scan activity (rowsScanned, status, times)
 *
 * Click-through model: KPI tiles + sections are router <Link>s into the
 * relevant page. Opening a SPECIFIC finding uses a sessionStorage handoff
 * (`discovery.focusFinding`) that Findings.tsx reads on mount - robust and
 * router-config-free. Severity drill-downs use `discovery.findingsSeverity`.
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  Database, AlertTriangle, ShieldAlert, FileSearch, Activity as ActivityIcon, GitBranch, Radar,
  Loader2, ArrowRight, ShieldOff, UserX, Gauge, Layers, HardDrive, ScanLine, ShieldCheck,
  TrendingUp, ChevronRight, MapPin,
} from 'lucide-react';
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { KpiCard } from '@/components/viz/KpiCard';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { DecisionHeader, type DecisionAction } from '@/components/viz/DecisionHeader';
import { PageGuide } from '@/components/ui/page-guide';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from '@tanstack/react-router';
import { riskScore, dedupeTopBy, type RiskBand } from '@/lib/risk-score';
import { adaptDtoToFinding } from './Findings';
import {
  listDiscoveryFindings,
  getDiscoveryCoverage,
  getDiscoveryRiskRanking,
  type RiskRankedAsset,
  listDiscoveryAssets,
  listDiscoveryScans,
  triggerDiscoveryScan,
  type DiscoveryFinding,
  type DiscoveryCoverage,
  type DiscoveryAsset,
  type DiscoveryScan,
} from '@/lib/api';

interface DashState {
  coverage: DiscoveryCoverage | null;
  findings: DiscoveryFinding[];
  assets: DiscoveryAsset[];
  scans: DiscoveryScan[];
  riskRanking: RiskRankedAsset[];
  error: string | null;
  loading: boolean;
}

const SEV_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, unknown: 0 };
const STALE_DAYS = 30;

// Static Tailwind class maps. Tailwind's JIT only emits classes it can see
// literally in source, and there is no safelist - so dynamic `bg-sev-${x}`
// strings (esp. opacity variants like `/15`) silently render with no colour.
// These maps keep every class as a literal the scanner will pick up.
const SEV_BG: Record<string, string> = { critical: 'bg-sev-critical', high: 'bg-sev-high', medium: 'bg-sev-medium', low: 'bg-sev-low' };
const TONE_BG: Record<'critical' | 'high' | 'medium' | 'low', string> = {
  critical: 'bg-sev-critical/15', high: 'bg-sev-high/15', medium: 'bg-sev-medium/15', low: 'bg-sev-low/15',
};
const TONE_TEXT: Record<'critical' | 'high' | 'medium' | 'low', string> = {
  critical: 'text-sev-critical', high: 'text-sev-high', medium: 'text-sev-medium', low: 'text-sev-low',
};
// Risk-band score chip colours. Static literals so Tailwind's JIT keeps them.
const RISK_BG: Record<string, string> = {
  critical: 'bg-sev-critical/20', high: 'bg-sev-high/20', medium: 'bg-sev-medium/20', low: 'bg-sev-low/20', minimal: 'bg-muted',
};
const RISK_TEXT: Record<string, string> = {
  critical: 'text-sev-critical', high: 'text-sev-high', medium: 'text-sev-medium', low: 'text-sev-low', minimal: 'text-muted-foreground',
};

/**
 * "Run scan" toolbar action - fires a sketch scan across every configured
 * connector at classifier tiers 1-4. Surfaces the scan id + status inline.
 */
function useRunScan() {
  const [running, setRunning] = useState(false);
  const [lastScan, setLastScan] = useState<DiscoveryScan | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const run = async () => {
    setRunning(true);
    setLastError(null);
    try {
      const scan = await triggerDiscoveryScan({ scanMode: 'sketch', classifierTiers: [1, 2, 3, 4] });
      setLastScan(scan);
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Scan trigger failed');
    } finally {
      setRunning(false);
    }
  };
  const dismiss = () => { setLastScan(null); setLastError(null); };
  return { running, lastScan, lastError, run, dismiss };
}

export function DiscoveryDashboard() {
  const scan = useRunScan();
  const navigate = useNavigate();
  const [state, setState] = useState<DashState>({
    coverage: null, findings: [], assets: [], scans: [], riskRanking: [], error: null, loading: true,
  });

  const refresh = (signal?: AbortSignal) =>
    Promise.allSettled([
      getDiscoveryCoverage(),
      listDiscoveryFindings({ limit: 200 }),
      listDiscoveryAssets(),
      listDiscoveryScans(),
      getDiscoveryRiskRanking(),
    ]).then(([coverageRes, findingsRes, assetsRes, scansRes, riskRes]) => {
      if (signal?.aborted) return;
      const coverage = coverageRes.status === 'fulfilled' ? coverageRes.value : null;
      const findings = findingsRes.status === 'fulfilled' ? findingsRes.value.findings : [];
      const assets = assetsRes.status === 'fulfilled' ? assetsRes.value.assets : [];
      const scans = scansRes.status === 'fulfilled' ? scansRes.value.scans : [];
      const riskRanking = riskRes.status === 'fulfilled' ? riskRes.value.items : [];
      const errors: string[] = [];
      if (coverageRes.status === 'rejected') errors.push(`coverage: ${(coverageRes.reason as Error)?.message ?? 'failed'}`);
      if (findingsRes.status === 'rejected') errors.push(`findings: ${(findingsRes.reason as Error)?.message ?? 'failed'}`);
      // assets/scans/risk are enrichment - degrade quietly rather than flagging the whole page "degraded".
      setState({ coverage, findings, assets, scans, riskRanking, error: errors.length ? errors.join(' · ') : null, loading: false });
    });

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  // ── navigation handoffs ──────────────────────────────────────────
  const openFinding = (id: string) => {
    try { sessionStorage.setItem('discovery.focusFinding', id); } catch { /* sessionStorage may be unavailable */ }
    navigate({ to: '/dspm/findings' });
  };
  const openFindingsBySeverity = (sev: string) => {
    try { sessionStorage.setItem('discovery.findingsSeverity', sev); } catch { /* ignore */ }
    navigate({ to: '/dspm/findings' });
  };

  // ── derived metrics (all real) ───────────────────────────────────
  const cov = state.coverage;
  const totalAssets = cov?.totalAssets ?? 0;
  const classifiedAssets = cov?.classifiedAssets ?? 0;
  const classifiedPct = totalAssets > 0 ? classifiedAssets / totalAssets : 0;

  const openFindings = state.findings.filter((f) => f.status === 'open').length;
  const criticalOpen = state.findings.filter((f) => f.status === 'open' && f.severity === 'critical').length;
  const section9Violations = state.findings.filter((f) => {
    const t = f.findingType?.toLowerCase() ?? '';
    return t.includes('section_9') || t.includes('minor') || f.classifications.some((c) => /minor|guardian|child/i.test(c.label));
  }).length;

  // Compliance coverage = the REAL data the /coverage endpoint returns
  // (per-framework coverageScore) + classification coverage. There is no
  // posture{dpdpa/rbi/sebi} object from the backend, so we don't fake one.
  const coverageScore = classifiedPct; // 0..1 - real classification coverage
  const coverageBand = scoreBand(coverageScore);

  const trend = useMemo(() => buildTrend(state.findings, 30), [state.findings]);
  // PII-by-type: prefer the canonical backend byCategory; fall back to deriving
  // it from real asset classifications when the endpoint doesn't send it.
  const piiDistribution = useMemo(() => {
    const cat = cov?.byCategory;
    if (cat && Object.keys(cat).length > 0) return distFromRecord(cat);
    return distFromRecord(assetsToCategoryRecord(state.assets));
  }, [cov, state.assets]);
  const severityData = useMemo(() => buildSeverity(cov?.bySeverity, state.findings), [cov, state.findings]);
  const sourceData = useMemo(() => buildBySource(state.assets), [state.assets]);
  const frameworkRows = useMemo(() => buildFrameworks(cov?.byFramework ?? [], state.findings), [cov, state.findings]);

  // data estate (from real assets)
  const estate = useMemo(() => buildEstate(state.assets), [state.assets]);
  const topRisks = useMemo(() => buildTopRisks(state.assets), [state.assets]);
  const lastScan = useMemo(() => buildLastScan(state.scans), [state.scans]);
  const recommendations = useMemo(
    () => buildRecommendations({ criticalOpen, unowned: estate.unowned, classifiedPct, totalAssets, section9Violations, stale: estate.stale, frameworks: cov?.byFramework ?? [] }),
    [criticalOpen, estate.unowned, estate.stale, classifiedPct, totalAssets, section9Violations, cov],
  );

  const recentCritical = useMemo(
    () => [...state.findings]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .filter((f) => f.severity === 'critical' || f.severity === 'high')
      .slice(0, 6),
    [state.findings],
  );

  // ── doc 33: decision-first posture + the 3 next-best-actions. Reuses the
  // Findings adapter + the shared risk score so the dashboard ranks the work
  // identically to the Findings triage queue. ─────────────────────────────
  const decision = useMemo(() => {
    const open = state.findings
      .map(adaptDtoToFinding)
      .filter((f) => f.status !== 'resolved' && f.status !== 'false_positive')
      .map((f) => ({ f, risk: riskScore(f) }))
      .sort((a, b) => b.risk.score - a.risk.score);
    // "open" here = unresolved work (not resolved, not false-positive) - the
    // same definition the Findings posture uses, so the two pages agree. This
    // can differ from the strict status==='open' KPI tile, which is fine: they
    // measure different things and are labelled differently.
    const critOpen = open.filter(({ f }) => f.severity === 'critical').length;
    const highOpen = open.filter(({ f }) => f.severity === 'high').length;
    const assetsAtRisk = new Set(open.map(({ f }) => f.asset)).size;
    // NOTE: the finding-level score here drives the next-best-ACTION ranking
    // only; the headline posture number comes from the canonical asset-level
    // score (headlinePosture below) so it matches the "Top data risks" card and
    // what Core ingests.
    return { open, critOpen, highOpen, assetsAtRisk };
  }, [state.findings]);

  // Headline DSPM posture = the canonical backend asset-level /risk-ranking
  // score (the same one in the "Top data risks" card + the board-summary Core
  // pulls). One number everywhere (founder decision 2026-06-23).
  const RISK_BANDS: readonly string[] = ['critical', 'high', 'medium', 'low'];
  const headlinePosture = useMemo(() => {
    const top = state.riskRanking[0];
    if (!top) return { score: 0, band: 'low' as RiskBand };
    return { score: top.score, band: (RISK_BANDS.includes(top.band) ? top.band : 'low') as RiskBand };
  }, [state.riskRanking]);
  // Roll up by asset+type so the 3 cards are 3 DISTINCT exposures, not repeats.
  const decisionActions: DecisionAction[] = dedupeTopBy(
    decision.open,
    ({ f }) => `${f.asset}::${f.type}`,
    3,
  ).map(({ item: { f, risk }, count }) => ({
    id: f.id,
    label: `${f.type.replace(/_/g, ' ')} in ${f.asset}`,
    detail: [
      count > 1 ? `${count} findings` : null,
      f.count !== null ? `${f.count.toLocaleString('en-IN')} records` : null,
      f.owner === 'unassigned' ? 'no owner' : f.owner,
    ]
      .filter(Boolean)
      .join(' · '),
    band: risk.band,
    onClick: () => openFinding(f.id),
  }));
  const decisionSummary =
    decision.open.length === 0
      ? 'No open exposures. Keep scanning to stay current.'
      : `${decision.critOpen} critical, ${decision.highOpen} high open across ${decision.assetsAtRisk} ${decision.assetsAtRisk === 1 ? 'asset' : 'assets'}.`;

  return (
    <PageContainer>
      <PageHeader
        title="Discovery"
        subtitle="Data Security Posture Management - find every PII column, prove what is at rest, ship Section 9 reports."
        badges={[
          { label: 'DSPM', variant: 'glass' },
          ...(state.loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : state.error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <>
            <Button variant="glass" size="sm" asChild title="Build a signed board / regulator report - your DSPM posture feeds straight into it">
              <Link to="/compliance/regulatory-coverage">Board report</Link>
            </Button>
            <Button variant="glass" size="sm" asChild>
              <Link to="/dspm/connectors">Add connector</Link>
            </Button>
            <Button size="sm" onClick={scan.run} disabled={scan.running} title="Trigger an incremental scan across all connectors (tier 1-4 classifiers)">
              {scan.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
              {scan.running ? 'Queueing scan…' : 'Run scan'}
            </Button>
          </>
        }
      />

      <PageGuide id="dashboard">
        This is your data-security overview. The big score is how exposed your sensitive data is right
        now (higher = worse), and the cards beside it are the most important things to fix first. The
        numbers and charts below are the detail behind it. New here? Start by running a scan.
      </PageGuide>

      {(scan.lastScan || scan.lastError) && (
        <div className={`glass-lite rounded-md p-3 mb-3 text-xs flex items-start gap-2 ${scan.lastError ? 'border border-sev-high/40' : 'border border-success/40'}`}>
          {scan.lastError ? <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" /> : <Radar className="h-4 w-4 text-success shrink-0 mt-0.5" />}
          <div className="flex-1">
            {scan.lastError ? (
              <><div className="font-medium">Scan trigger failed</div><div className="text-muted-foreground mt-0.5">{scan.lastError}</div></>
            ) : scan.lastScan ? (
              <><div className="font-medium">Scan queued</div>
                <div className="text-muted-foreground mt-0.5">ID: <code className="font-mono text-foreground/80">{scan.lastScan.id}</code> · status: {scan.lastScan.status}. Findings will appear as the scan progresses.</div></>
            ) : null}
          </div>
          <Button size="sm" variant="glass" onClick={scan.dismiss}>Dismiss</Button>
        </div>
      )}

      {state.loading && <LoadingState caption="Loading dashboard…" />}

      {state.error && !state.loading && (
        <ErrorState title="Some data unavailable" detail={state.error}
          onRetry={() => { setState((s) => ({ ...s, error: null, loading: true })); refresh(); }} />
      )}

      {!state.loading && (
        <>
          {/* ── Decision header (doc 33): lead with the risk + the next actions ── */}
          <DecisionHeader
            score={headlinePosture.score}
            band={headlinePosture.band}
            scoreLabel="Data risk posture"
            actions={decisionActions}
            summary={decisionSummary}
          />

          {/* ── Row 1: clickable, risk-ranked KPIs ─────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <ClickableKpi to="/dspm/inventory" label="Total assets" value={totalAssets} icon={Database} hint={`${classifiedAssets} classified`} />
            <ClickableKpi onClick={() => openFindingsBySeverity('all')} label="Open findings" value={openFindings} icon={AlertTriangle} glow={criticalOpen > 0 ? 'critical' : 'medium'} hint={criticalOpen > 0 ? `${criticalOpen} critical` : 'none critical'} />
            <ClickableKpi onClick={() => openFindingsBySeverity('critical')} label="Critical open" value={criticalOpen} icon={ShieldAlert} glow={criticalOpen > 0 ? 'critical' : 'low'} hint="needs action now" />
            <ClickableKpi to="/dspm/section-9" label="Section 9" value={section9Violations} icon={FileSearch} glow={section9Violations > 0 ? 'critical' : 'low'} hint="DPDPA - minor data" />
            <ClickableKpi to="/dspm/inventory" label="Classified" value={classifiedPct} format="percent" icon={ShieldCheck} glow={classifiedPct >= 0.85 ? 'high' : 'medium'} hint={`${classifiedAssets}/${totalAssets} assets`} />
          </div>

          {/* ── Row 2: exposure trend + security posture ────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <GlassCard className="lg:col-span-2 p-6">
              <CardHeader className="p-0 mb-4 flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Exposure over 30 days</CardTitle>
                  <CardDescription>New findings by detected-at - critical / high / medium / low</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => openFindingsBySeverity('all')}>View findings <ChevronRight className="h-3 w-3" /></Button>
              </CardHeader>
              {state.findings.length === 0 ? (
                <EmptyHint>No findings yet - run a discovery scan to begin tracking exposure.</EmptyHint>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={trend}>
                    <defs>
                      {(['crit', 'high', 'med', 'low'] as const).map((k, i) => (
                        <linearGradient key={k} id={`g-${k}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={`hsl(var(--sev-${['critical', 'high', 'medium', 'low'][i]}))`} stopOpacity={0.85} />
                          <stop offset="100%" stopColor={`hsl(var(--sev-${['critical', 'high', 'medium', 'low'][i]}))`} stopOpacity={0.08} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                    <XAxis dataKey="day" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <YAxis allowDecimals={false} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                    <Area type="monotone" dataKey="critical" stackId="1" stroke="hsl(var(--sev-critical))" fill="url(#g-crit)" strokeWidth={2} />
                    <Area type="monotone" dataKey="high" stackId="1" stroke="hsl(var(--sev-high))" fill="url(#g-high)" strokeWidth={2} />
                    <Area type="monotone" dataKey="medium" stackId="1" stroke="hsl(var(--sev-medium))" fill="url(#g-med)" strokeWidth={2} />
                    <Area type="monotone" dataKey="low" stackId="1" stroke="hsl(var(--sev-low))" fill="url(#g-low)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </GlassCard>

            {/* Compliance coverage - REAL data: classification coverage ring +
                per-framework coverage (byFramework) with live open-finding counts. */}
            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-4 flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" /> Compliance coverage</CardTitle>
                  <CardDescription>Classification coverage + regulatory frameworks</CardDescription>
                </div>
                <Button variant="ghost" size="sm" asChild><Link to="/compliance"><ChevronRight className="h-3 w-3" /></Link></Button>
              </CardHeader>
              <div className="flex items-center gap-4 mb-5">
                <div className="relative h-20 w-20 shrink-0">
                  <ScoreRing value={coverageScore} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-xl font-display font-bold tabular-nums">{Math.round(coverageScore * 100)}</span>
                    <span className="text-[9px] text-muted-foreground -mt-0.5">% classified</span>
                  </div>
                </div>
                <div>
                  <div className={`text-sm font-semibold ${coverageBand.color}`}>{coverageBand.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{coverageBand.hint}</div>
                </div>
              </div>
              {frameworkRows.length === 0 ? (
                <EmptyHint>No framework coverage yet - run a scan to populate.</EmptyHint>
              ) : (
                <div className="space-y-2.5">
                  {frameworkRows.map((fw) => (
                    <div key={fw.framework}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">{fw.framework.replace(/_/g, ' ')}</span>
                        <span className="flex items-center gap-2">
                          {fw.openFindings > 0 && <Badge variant="high" className="text-[9px]">{fw.openFindings} open</Badge>}
                          <span className="font-mono tabular-nums">{Math.round(fw.coverageScore * 100)}%</span>
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
                        <motion.div className={`h-full rounded-full ${fw.coverageScore >= 0.8 ? 'bg-success' : fw.coverageScore >= 0.5 ? 'bg-sev-medium' : 'bg-sev-critical'}`}
                          initial={{ width: 0 }} animate={{ width: `${Math.round(fw.coverageScore * 100)}%` }} transition={{ duration: 0.8, ease: 'easeOut' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
          </div>

          {/* ── Row 3: risk by severity + PII types + data estate by source ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-primary" /> Risk by severity</CardTitle>
                <CardDescription>Findings by severity - click a band to drill in</CardDescription>
              </CardHeader>
              {severityData.total === 0 ? (
                <EmptyHint>No findings to rank yet.</EmptyHint>
              ) : (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="55%" height={170}>
                    <PieChart>
                      <Pie data={severityData.data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2} stroke="none">
                        {severityData.data.map((d) => (
                          <Cell key={d.key} fill={`hsl(var(--sev-${d.key}))`} className="cursor-pointer" onClick={() => openFindingsBySeverity(d.key)} />
                        ))}
                      </Pie>
                      <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5">
                    {severityData.data.map((d) => (
                      <button key={d.key} onClick={() => openFindingsBySeverity(d.key)}
                        className="w-full flex items-center justify-between text-xs rounded-md px-2 py-1 hover:bg-muted/40 transition-colors">
                        <span className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${SEV_BG[d.key] ?? 'bg-sev-medium'}`} /> {d.name}</span>
                        <span className="font-mono tabular-nums text-foreground/80">{d.value}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </GlassCard>

            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle className="flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> Sensitive data by type</CardTitle>
                <CardDescription>PII categories across {totalAssets.toLocaleString('en-IN')} assets</CardDescription>
              </CardHeader>
              {piiDistribution.length === 0 ? (
                <EmptyHint>No classified PII yet - run a discovery scan to populate.</EmptyHint>
              ) : (
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <Pie data={piiDistribution} dataKey="value" nameKey="name" innerRadius={42} outerRadius={72} paddingAngle={2} stroke="none">
                      {piiDistribution.map((_, i) => (<Cell key={i} fill={`hsl(var(--chart-${(i % 8) + 1}))`} />))}
                    </Pie>
                    <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
              {piiDistribution.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {piiDistribution.slice(0, 6).map((p, i) => (
                    <span key={p.name} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span className="h-2 w-2 rounded-full" style={{ background: `hsl(var(--chart-${(i % 8) + 1}))` }} /> {p.name}
                    </span>
                  ))}
                </div>
              )}
            </GlassCard>

            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3 flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><Database className="h-4 w-4 text-primary" /> Data estate by source</CardTitle>
                  <CardDescription>Where your classified data lives</CardDescription>
                </div>
                <Button variant="ghost" size="sm" asChild><Link to="/dspm/inventory">Inventory <ChevronRight className="h-3 w-3" /></Link></Button>
              </CardHeader>
              {sourceData.length === 0 ? (
                <EmptyHint>No connected sources yet - add a connector to begin.</EmptyHint>
              ) : (
                <ResponsiveContainer width="100%" height={170}>
                  <BarChart data={sourceData} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                    <XAxis type="number" allowDecimals={false} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <YAxis type="category" dataKey="name" width={88} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.2 }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </GlassCard>
          </div>

          {/* ── Row 4: top data risks + recommended actions ────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3 flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><HardDrive className="h-4 w-4 text-primary" /> Top data risks</CardTitle>
                  <CardDescription>Ranked by unified data-risk score (sensitivity + exposure + secrets + ownership)</CardDescription>
                </div>
                <Button variant="ghost" size="sm" asChild><Link to="/dspm/inventory">All assets <ChevronRight className="h-3 w-3" /></Link></Button>
              </CardHeader>
              {state.riskRanking.length > 0 ? (
                <div className="space-y-1.5">
                  {state.riskRanking.slice(0, 6).map((a, i) => (
                    <motion.div key={a.assetRef} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04, duration: 0.3 }}>
                      <Link to="/dspm/inventory" className="flex items-center gap-3 p-2.5 rounded-md hover:bg-muted/40 transition-colors group">
                        <span className={`shrink-0 w-9 h-9 rounded-md grid place-items-center text-xs font-bold tabular ${RISK_BG[a.band] ?? 'bg-muted'} ${RISK_TEXT[a.band] ?? ''}`}>{a.score}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{a.name}</div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {a.topFactors[0] ?? a.sourceType}{a.topFactors.length > 1 ? ` · +${a.topFactors.length - 1} more` : ''}
                          </div>
                        </div>
                        <Badge variant={(a.band === 'minimal' ? 'glass' : a.band) as 'critical' | 'high' | 'medium' | 'low' | 'glass'} className="text-[9px] shrink-0 capitalize">{a.band}</Badge>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : topRisks.length === 0 ? (
                <EmptyHint>No classified assets yet.</EmptyHint>
              ) : (
                <div className="space-y-1.5">
                  {topRisks.map((a, i) => (
                    <motion.div key={a.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04, duration: 0.3 }}>
                      <Link to="/dspm/inventory" className="flex items-center gap-3 p-2.5 rounded-md hover:bg-muted/40 transition-colors group">
                        <Badge variant={(a.sensitivity === 'unknown' ? 'glass' : a.sensitivity) as 'critical' | 'high' | 'medium' | 'low' | 'glass'} className="text-[9px] shrink-0">{a.sensitivity}</Badge>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{a.displayName}</div>
                          <div className="text-[11px] text-muted-foreground truncate flex items-center gap-2">
                            <span>{a.sourceType}</span>
                            {a.rowCount != null && <span>· {a.rowCount.toLocaleString('en-IN')} rows</span>}
                            {a.ownerEmail ? <span className="truncate">· {a.ownerEmail}</span> : <span className="text-sev-high inline-flex items-center gap-0.5"><UserX className="h-3 w-3" /> unowned</span>}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </GlassCard>

            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Recommended actions</CardTitle>
                <CardDescription>Prioritised next steps to reduce exposure</CardDescription>
              </CardHeader>
              {recommendations.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-8 gap-2">
                  <ShieldCheck className="h-8 w-8 text-success" />
                  <div className="text-sm font-medium">Posture looks healthy</div>
                  <div className="text-xs text-muted-foreground">No critical gaps detected from current data.</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {recommendations.map((r, i) => (
                    <motion.div key={r.key} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05, duration: 0.3 }}>
                      <Link to={r.to} className="flex items-start gap-3 p-3 rounded-md border border-border/50 hover:border-primary/50 hover:bg-muted/30 transition-colors group">
                        <span className={`mt-0.5 h-7 w-7 rounded-md grid place-items-center shrink-0 ${TONE_BG[r.tone]}`}>
                          <r.icon className={`h-4 w-4 ${TONE_TEXT[r.tone]}`} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{r.title}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{r.detail}</div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors mt-0.5 shrink-0" />
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </GlassCard>
          </div>

          {/* ── Row 5: recent high-severity findings + data-at-rest snapshot ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3 flex-row items-center justify-between">
                <div>
                  <CardTitle>Recent high-severity findings</CardTitle>
                  <CardDescription>Newest critical + high - click to open the finding</CardDescription>
                </div>
                <Button variant="ghost" size="sm" asChild><Link to="/dspm/findings">View all</Link></Button>
              </CardHeader>
              <div className="space-y-1">
                {recentCritical.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-6 text-center">No high-severity findings open. 🎉</div>
                ) : (
                  recentCritical.map((f, i) => (
                    <motion.button key={f.id} type="button" onClick={() => openFinding(f.id)}
                      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04, duration: 0.3 }}
                      className="w-full text-left flex items-center gap-3 p-3 rounded-md hover:bg-muted/40 cursor-pointer transition-colors group">
                      <span className={`h-2 w-2 rounded-full ${SEV_BG[f.severity] ?? 'bg-sev-medium'} animate-pulse shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{f.title || f.findingType.replace(/_/g, ' ')}</span>
                          <Badge variant={f.severity as 'critical' | 'high' | 'medium' | 'low'} className="text-[9px] shrink-0">{f.severity}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{timeAgo(f.createdAt)}{f.relatedRegulations.length ? ` · ${f.relatedRegulations.slice(0, 2).join(', ')}` : ''}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                    </motion.button>
                  ))
                )}
              </div>
            </GlassCard>

            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-4">
                <CardTitle className="flex items-center gap-2"><ScanLine className="h-4 w-4 text-primary" /> Sensitive data at rest</CardTitle>
                <CardDescription>Estate snapshot + last scan activity</CardDescription>
              </CardHeader>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <StatTile label="Rows at rest" value={estate.totalRows > 0 ? compactNum(estate.totalRows) : '-'} sub="classified assets" />
                <StatTile label="Data volume" value={estate.totalBytes > 0 ? formatBytes(estate.totalBytes) : '-'} sub="on disk" />
                <StatTile label="Unowned assets" value={String(estate.unowned)} sub="no data owner" tone={estate.unowned > 0 ? 'high' : undefined} />
                <StatTile label="Stale (>30d)" value={String(estate.stale)} sub="need a rescan" tone={estate.stale > 0 ? 'medium' : undefined} />
              </div>
              <div className="rounded-md border border-border/50 p-3 flex items-center gap-3">
                <ScanLine className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0 text-xs">
                  {lastScan ? (
                    <>
                      <span className="text-foreground/90">Last scan {timeAgo(lastScan.at)}</span>
                      <span className="text-muted-foreground"> · {lastScan.rowsScanned.toLocaleString('en-IN')} rows · {lastScan.status}</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">No scans run yet.</span>
                  )}
                </div>
                <Button variant="glass" size="sm" asChild><Link to="/dspm/scans">Scans</Link></Button>
              </div>
              {estate.residency.length > 1 && (
                <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <MapPin className="h-3 w-3" /> Residency: {estate.residency.map((r) => `${r.country} ${r.count}`).join(' · ')}
                </div>
              )}
            </GlassCard>
          </div>

          {/* ── Quick links ─────────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <QuickLink to="/dspm/findings" icon={AlertTriangle} label="Findings" />
            <QuickLink to="/dspm/inventory" icon={Database} label="Inventory" />
            <QuickLink to="/dspm/lineage" icon={GitBranch} label="Lineage" />
            <QuickLink to="/dspm/section-9" icon={FileSearch} label="Children's data" />
            <QuickLink to="/dspm/reverse-dsar" icon={ActivityIcon} label="Find a person's data" />
            <QuickLink to="/dspm/no-db-access" icon={ShieldOff} label="Scan without credentials" />
          </div>
        </>
      )}
    </PageContainer>
  );
}

// ── presentational helpers ─────────────────────────────────────────

function ClickableKpi({
  to, onClick, ...kpi
}: { to?: string; onClick?: () => void } & React.ComponentProps<typeof KpiCard>) {
  const cardClass = 'transition-all duration-200 hover:ring-2 hover:ring-primary/40 hover:-translate-y-0.5 cursor-pointer h-full';
  if (to) {
    return <Link to={to} className="group block h-full"><KpiCard {...kpi} className={cardClass} /></Link>;
  }
  return (
    <button type="button" onClick={onClick} className="group block h-full text-left w-full">
      <KpiCard {...kpi} className={cardClass} />
    </button>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <div className="text-xs text-muted-foreground py-8 text-center">{children}</div>;
}

function StatTile({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: 'high' | 'medium' }) {
  const toneClass = tone === 'high' ? 'text-sev-high' : tone === 'medium' ? 'text-sev-medium' : '';
  return (
    <div className="rounded-md border border-border/50 p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-display font-bold tabular-nums mt-0.5 ${toneClass}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{sub}</div>
    </div>
  );
}

/** SVG ring for the overall coverage score (0..1). */
function ScoreRing({ value }: { value: number }) {
  const r = 34, c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, value));
  const tone = pct >= 0.8 ? 'var(--success)' : pct >= 0.5 ? 'var(--sev-medium)' : 'var(--sev-critical)';
  return (
    <svg viewBox="0 0 80 80" className="h-20 w-20 -rotate-90">
      <circle cx="40" cy="40" r={r} fill="none" stroke="hsl(var(--muted))" strokeOpacity={0.4} strokeWidth="7" />
      <motion.circle cx="40" cy="40" r={r} fill="none" stroke={`hsl(${tone})`} strokeWidth="7" strokeLinecap="round"
        strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c * (1 - pct) }} transition={{ duration: 1, ease: 'easeOut' }} />
    </svg>
  );
}

function QuickLink({ to, icon: Icon, label }: { to: string; icon: React.ComponentType<{ className?: string }>; label: string }) {
  return (
    <Link to={to}>
      <GlassCard className="p-4 flex flex-col items-center gap-2 lift cursor-pointer">
        <Icon className="h-5 w-5 text-primary" />
        <span className="text-xs font-medium text-center">{label}</span>
      </GlassCard>
    </Link>
  );
}

// ── pure data helpers ──────────────────────────────────────────────

function scoreBand(score: number): { label: string; color: string; hint: string } {
  if (score >= 0.85) return { label: 'Strong', color: 'text-success', hint: 'Most sensitive data is classified and governed.' };
  if (score >= 0.6) return { label: 'Good', color: 'text-success', hint: 'Solid coverage - a few gaps remain to close.' };
  if (score >= 0.4) return { label: 'Fair', color: 'text-sev-medium', hint: 'Meaningful exposure - prioritise the actions below.' };
  return { label: 'At risk', color: 'text-sev-critical', hint: 'Low coverage - sensitive data may be ungoverned.' };
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function compactNum(n: number): string {
  return Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let v = b / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

interface TrendBucket { day: string; critical: number; high: number; medium: number; low: number }

function buildTrend(findings: DiscoveryFinding[], days: number): TrendBucket[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const buckets = new Map<string, TrendBucket>();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    buckets.set(key, { day: key.slice(5), critical: 0, high: 0, medium: 0, low: 0 });
  }
  for (const f of findings) {
    const bucket = buckets.get(f.createdAt.slice(0, 10));
    if (!bucket) continue;
    if (f.severity === 'critical') bucket.critical++;
    else if (f.severity === 'high') bucket.high++;
    else if (f.severity === 'low') bucket.low++;
    else bucket.medium++;
  }
  return Array.from(buckets.values());
}

/** Count classification labels across the real asset inventory (fallback for
 *  PII-by-type when the /coverage byCategory isn't populated). */
function assetsToCategoryRecord(assets: DiscoveryAsset[]): Record<string, number> {
  const m: Record<string, number> = {};
  for (const a of assets) {
    for (const c of a.classifications) {
      const label = (c.label ?? '').trim();
      if (label) m[label] = (m[label] ?? 0) + 1;
    }
  }
  return m;
}

/** Reshape a {label: count} record into the recharts pie input - top 7 +
 *  "Other PII" so the legend stays readable. */
function distFromRecord(byCategory: Record<string, number>): Array<{ name: string; value: number }> {
  const entries = Object.entries(byCategory).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) return [];
  const top = entries.slice(0, 7);
  const tail = entries.slice(7).reduce((sum, [, v]) => sum + v, 0);
  return [...top.map(([name, value]) => ({ name, value })), ...(tail > 0 ? [{ name: 'Other PII', value: tail }] : [])];
}

interface FrameworkRow { framework: string; coverageScore: number; openFindings: number }

/** Per-framework coverage rows - coverageScore from the real /coverage
 *  byFramework, open-finding counts re-derived from the live findings'
 *  relatedRegulations (more accurate than the endpoint's stubbed 0). DPDPA
 *  first (India-first product), then by open findings, capped at 6. */
function buildFrameworks(
  byFramework: DiscoveryCoverage['byFramework'],
  findings: DiscoveryFinding[],
): FrameworkRow[] {
  const openByReg = new Map<string, number>();
  for (const f of findings) {
    if (f.status !== 'open') continue;
    for (const r of f.relatedRegulations) openByReg.set(r, (openByReg.get(r) ?? 0) + 1);
  }
  const rows = byFramework.map((fw) => ({
    framework: fw.framework,
    coverageScore: fw.coverageScore,
    openFindings: openByReg.get(fw.framework) ?? fw.openFindings ?? 0,
  }));
  rows.sort((a, b) => {
    if (a.framework === 'DPDPA') return -1;
    if (b.framework === 'DPDPA') return 1;
    return b.openFindings - a.openFindings;
  });
  return rows.slice(0, 6);
}

/** Severity breakdown - prefer coverage.bySeverity; fall back to counting findings. */
function buildSeverity(
  bySeverity: DiscoveryCoverage['bySeverity'] | undefined,
  findings: DiscoveryFinding[],
): { data: Array<{ key: string; name: string; value: number }>; total: number } {
  const order = ['critical', 'high', 'medium', 'low'] as const;
  const counts: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0 };
  if (bySeverity && Object.values(bySeverity).some((v) => v > 0)) {
    for (const k of order) counts[k] = bySeverity[k] ?? 0;
  } else {
    for (const f of findings) {
      const k = order.includes(f.severity as typeof order[number]) ? f.severity : 'medium';
      counts[k] = (counts[k] ?? 0) + 1;
    }
  }
  const data = order.map((k) => ({ key: k, name: k[0].toUpperCase() + k.slice(1), value: counts[k] })).filter((d) => d.value > 0);
  return { data, total: data.reduce((s, d) => s + d.value, 0) };
}

function buildBySource(assets: DiscoveryAsset[]): Array<{ name: string; value: number }> {
  const m = new Map<string, number>();
  for (const a of assets) m.set(a.sourceType, (m.get(a.sourceType) ?? 0) + 1);
  return Array.from(m.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
}

interface Estate {
  totalRows: number; totalBytes: number; unowned: number; stale: number;
  residency: Array<{ country: string; count: number }>;
}

function buildEstate(assets: DiscoveryAsset[]): Estate {
  const staleCutoff = Date.now() - STALE_DAYS * 86400000;
  let totalRows = 0, totalBytes = 0, unowned = 0, stale = 0;
  const residency = new Map<string, number>();
  for (const a of assets) {
    const classified = a.classifications.length > 0;
    if (classified) {
      totalRows += a.rowCount ?? 0;
      totalBytes += a.sizeBytes ?? 0;
    }
    if (!a.ownerEmail) unowned++;
    const last = a.lastScannedAt ? new Date(a.lastScannedAt).getTime() : 0;
    if (last < staleCutoff) stale++;
    if (a.countryCode) residency.set(a.countryCode, (residency.get(a.countryCode) ?? 0) + 1);
  }
  return {
    totalRows, totalBytes, unowned, stale,
    residency: Array.from(residency.entries()).map(([country, count]) => ({ country, count })).sort((x, y) => y.count - x.count),
  };
}

interface TopRisk { id: string; displayName: string; sourceType: string; sensitivity: string; ownerEmail: string | null; rowCount: number | null }

function buildTopRisks(assets: DiscoveryAsset[]): TopRisk[] {
  return [...assets]
    .sort((a, b) => {
      const sr = (SEV_RANK[b.sensitivity] ?? 0) - (SEV_RANK[a.sensitivity] ?? 0);
      if (sr !== 0) return sr;
      return (b.rowCount ?? 0) - (a.rowCount ?? 0);
    })
    .slice(0, 6)
    .map((a) => ({ id: a.id, displayName: a.displayName, sourceType: a.sourceType, sensitivity: a.sensitivity, ownerEmail: a.ownerEmail, rowCount: a.rowCount }));
}

function buildLastScan(scans: DiscoveryScan[]): { at: string; rowsScanned: number; status: string } | null {
  if (scans.length === 0) return null;
  const sorted = [...scans].sort((a, b) => {
    const at = new Date(a.completedAt ?? a.createdAt).getTime();
    const bt = new Date(b.completedAt ?? b.createdAt).getTime();
    return bt - at;
  });
  const s = sorted[0];
  return { at: s.completedAt ?? s.createdAt, rowsScanned: s.rowsScanned, status: s.status };
}

interface Recommendation {
  key: string; title: string; detail: string; to: string;
  tone: 'critical' | 'high' | 'medium' | 'low'; icon: React.ComponentType<{ className?: string }>;
}

function buildRecommendations(args: {
  criticalOpen: number; unowned: number; classifiedPct: number; totalAssets: number;
  section9Violations: number; stale: number; frameworks: DiscoveryCoverage['byFramework'];
}): Recommendation[] {
  const recs: Recommendation[] = [];
  if (args.criticalOpen > 0) {
    recs.push({ key: 'crit', tone: 'critical', icon: ShieldAlert, to: '/dspm/findings',
      title: `Resolve ${args.criticalOpen} critical finding${args.criticalOpen > 1 ? 's' : ''}`,
      detail: 'Critical exposures are open and need immediate triage.' });
  }
  if (args.section9Violations > 0) {
    recs.push({ key: 's9', tone: 'critical', icon: FileSearch, to: '/dspm/section-9',
      title: `Review ${args.section9Violations} Section 9 (minor data) item${args.section9Violations > 1 ? 's' : ''}`,
      detail: 'DPDPA §9 - minor data carries ₹200 Cr per-violation exposure.' });
  }
  if (args.unowned > 0) {
    recs.push({ key: 'owner', tone: 'high', icon: UserX, to: '/dspm/inventory',
      title: `Assign owners to ${args.unowned} unowned asset${args.unowned > 1 ? 's' : ''}`,
      detail: 'Orphaned data has no accountable owner for DSAR or breach response.' });
  }
  if (args.totalAssets > 0 && args.classifiedPct < 0.85) {
    recs.push({ key: 'cov', tone: 'medium', icon: ShieldCheck, to: '/dspm/scans',
      title: `Improve classification coverage (${Math.round(args.classifiedPct * 100)}%)`,
      detail: 'Run a deeper scan so unclassified assets don’t hide sensitive data.' });
  }
  if (args.stale > 0) {
    recs.push({ key: 'stale', tone: 'medium', icon: ScanLine, to: '/dspm/scans',
      title: `Rescan ${args.stale} stale asset${args.stale > 1 ? 's' : ''}`,
      detail: `Not scanned in ${STALE_DAYS}+ days - posture may be out of date.` });
  }
  const dpdpa = args.frameworks.find((f) => f.framework === 'DPDPA');
  if (dpdpa && dpdpa.coverageScore < 0.8) {
    recs.push({ key: 'dpdpa', tone: 'low', icon: Gauge, to: '/compliance',
      title: 'Strengthen DPDPA coverage',
      detail: `DPDPA coverage is ${Math.round(dpdpa.coverageScore * 100)}% - review the compliance centre.` });
  }
  return recs.slice(0, 5);
}
